import { CheckoutSelectors, CheckoutService, CheckoutStoreSelector, ConsignmentsRequestBody, CustomerAddress, StoreProfile } from '@bigcommerce/checkout-sdk'
import axios from 'axios'

import { mapCustomFormFieldsFromFormValues } from '../../formFields';
import addresses from '../addresses'
import { permissions } from '../config'
import extrasFiles from '../extrasFiles';
import { setAddressExtrasFiles } from '../extrasFiles/address'
import request from '../request'
import { AddressInfo, Country, getCompanyBasicInfo , getStoreCheckoutSettings } from '../request/apis'
import store from '../store'
import { setBasicAddressInfo, setCheckoutSettings, setCheckoutShippingIsComplete, setCompanyBasicInfo, setGlobalIsB3User } from '../store/actions'
import { constants, storage, util } from '../utils'
import { B3SetField } from '../utils/util'

import b3Login, { salesRepVerify } from './b3Login'
import {
  bcUserQuoteDefinition,
  determineOrderType,
  getAddressConfig,
  handleClosingAgency,
  salesRepDefinition,
  setCompanyCredit,
  userQuoteDefinition,
} from './utils'

const getQuoteData = async(data: CheckoutStoreSelector, quoteId: string, service: CheckoutService) => {
    const { global: { checkoutShippingIsComplete } } = store.getState()

    if (checkoutShippingIsComplete) return

    const config = data.getConfig()
    let isShippingAddressComplete = true
    const quoteDate = storage.quoteDate.getValue() || ''

    const quoteDetails: any = await request.api.getQuoteDetails(quoteId, {
      storeHash: (config?.storeProfile as any).storeHash,
      isBackendUser: '0',
      date: quoteDate
    })

    if (quoteDetails?.companyInfo?.companyId) {
      storage.quoteCompanyId.setValue(quoteDetails.companyInfo.companyId)
      storage.quoteCompanyName.setValue(quoteDetails.companyInfo.companyName)
    } else {
      storage.quoteCompanyId.removeValue()
      storage.quoteCompanyName.removeValue()
    }

    if (quoteDetails?.shippingAddress?.addressId) storage.shippingQuoteAddressId.setValue(quoteDetails?.shippingAddress?.addressId)

    if (quoteDetails?.billingAddress?.addressId) storage.billingQuoteAddressId.setValue(quoteDetails?.billingAddress?.addressId)

    if (!quoteDetails?.shippingAddress?.countryCode) return

    const getAddressFields = data.getShippingAddressFields(quoteDetails?.shippingAddress?.countryCode)

    const dataConvert: any = {
      'firstName': 'firstName',
      'lastName': 'lastName',
      'company': 'company',
      'phone': 'phoneNumber',
      'address1': 'address',
      'address2': 'apartment',
      'city': 'city',
      'countryCode': 'countryCode',
      'stateOrProvince': 'state',
      'postalCode': 'zipCode',
    }

    isShippingAddressComplete = getAddressFields.every((item: any) => {
      const quoteName: any = dataConvert[item.name]

      if (item.custom) return true

      if (item.name !=='company') {
        if ((item.required && quoteDetails?.shippingAddress[quoteName]) || !item.required) return true
      }
      
      if (item.name ==='company') {
        if ((item.required && (quoteDetails?.companyInfo?.companyName || quoteDetails?.contactInfo?.name)) || !item.required) return true
      }

      return false
    })

    storage.B3QuoteLabel.setValue('')

    if (isShippingAddressComplete) {
      const basicAddressInfo = util.parseAddress({
        companyName: quoteDetails.companyInfo.companyName || quoteDetails.contactInfo.name,
        addressLine1: quoteDetails.shippingAddress.address || '',
        addressLine2: quoteDetails.shippingAddress.apartment || '',
        city: quoteDetails.shippingAddress.city || '',
        state: {
          stateCode: quoteDetails.shippingAddress.stateCode || '',
          stateName: quoteDetails.shippingAddress.state || '',
        },
        country: {
          countryName: quoteDetails.shippingAddress.country || '',
          countryCode: quoteDetails.shippingAddress.countryCode || '',
        },
        zipCode: quoteDetails.shippingAddress.zipCode || '',
        phoneNumber: quoteDetails.shippingAddress.phoneNumber || '',
        type: "residential",
        addressId: '',
        firstName: quoteDetails.shippingAddress.firstName || '',
        lastName: quoteDetails.shippingAddress.lastName || '',
        label: quoteDetails.shippingAddress.label || '',
        isShipping: 1,
        isBilling: 1,
        customFields: [],
      })
      const { global: { isB3User } } = store.getState()

      if (!isB3User) {
        const consignments = data.getConsignments() ?? []
        const cart = data.getCart()

        const promises: Array<Promise<CheckoutSelectors>> = []

        if (!consignments.length) {
          const physicalItems = cart?.lineItems.physicalItems ?? []
    
          const digitalItems = cart?.lineItems.digitalItems ?? []

          if (digitalItems.length && !physicalItems.length) return
    
          const defaultConsignment = physicalItems ? [{
            lineItems: physicalItems.map(({ id: itemId, quantity }) => ({
              itemId,
              quantity,
            })),
            shippingAddress: basicAddressInfo
          }] : []

          promises.push(service.createConsignments(defaultConsignment as ConsignmentsRequestBody))
          await Promise.all(promises)
        }
      } else {
        store.dispatch(setBasicAddressInfo(basicAddressInfo as CustomerAddress))
      }

      storage.B3QuoteLabel.setValue(basicAddressInfo?.label || '')
      storage.quoteShippingMethods.setValue(JSON.stringify(quoteDetails.shippingMethod))
    }
}


const init = async (data: CheckoutStoreSelector, service: CheckoutService) => {
  const customer = data.getCustomer()
  const config = data.getConfig()
  const checkout = data.getCheckout()

  // Clear the following three fields
  storage.IPCheckout.setValue('')
  localStorage.setItem('invoicePay', '')
  
  if (!customer || !customer.id) {
    await initStoreCheckoutSettings()
  }


  try {
    const b3RoleId = storage.B3RoleId.getValue()

    if ((!b3RoleId || b3RoleId === constants.B3Role.SALESREP) && customer?.id) await b3Login(customer.id, (config?.storeProfile as StoreProfile).storeHash, checkout)
  } catch (error) {
    console.error(error)
  }

  const { global: { isB3User } } = store.getState()

  // Call the interface to obtain, if there is a session storage space
  if (customer?.id) await determineOrderType(data)

  if (isB3User) {
    await extrasFiles.getB3AddressFiles()
    await extrasFiles.getB3OrderFiles()
  } else {
    // solve b2b => bc  Flash display extras
    if (storage.B3OrderExtras.getValue()) storage.B3OrderExtras.removeValue()

    if (storage.B3DefaultAddressExtras.getValue()) {
      storage.B3BillingAddressExtras.removeValue()
      storage.B3ShippingAddressExtras.removeValue()
    }
  }

  await initStoreCheckoutSettings()

  // Judging the quote to get the interface bc
  const quoteId = storage.quoteCheckoutId.getValue() || ''

  if (quoteId) {
    await getQuoteData(data, quoteId, service)
  }

  if (!isB3User) {
    await bcUserQuoteDefinition()

    return
  }

  try {

    if (storage.B3RoleId.getValue() === constants.B3Role.SALESREP) {
      await salesRepDefinition()
    } else {
      await userQuoteDefinition()
    }

    await getAddressConfig(data)
    await setBasicInfo(data)
    await addresses.init(data)
    await Promise.all([initDefaultShipping(data, service), setCompanyCredit(data)])
  } finally {}
}

const setBasicInfo = async (data: CheckoutStoreSelector) => {
  const companyId = storage.B3CompanyId.getValue()

  const companyBasicInfo = await getCompanyBasicInfo(companyId)


  const {
    companyName,
    addressLine1,
    addressLine2,
    city,
    state,
    country,
    zipCode,
    phoneNumber,
  } = companyBasicInfo

  const customerAccountFields = data
    .getShippingAddressFields((country as Country).countryCode)
    .filter(field => field.custom)
    .reduce((result, { id, default: defaultValue, fieldType }) => {
      (result as any)[id] = B3SetField(defaultValue, fieldType)

      return result
    }, {})

  const basicAddressInfo = util.parseAddress({
    companyName,
    addressLine1,
    addressLine2,
    city,
    state,
    country,
    zipCode,
    phoneNumber,
    type: "residential",
    addressId: '',
    firstName: '',
    lastName: '',
    label: '',
    isShipping: 1,
    isBilling: 1,
    customFields: mapCustomFormFieldsFromFormValues(customerAccountFields),
  })

  store.dispatch(setCompanyBasicInfo(companyBasicInfo))

  const quoteId = storage.quoteCheckoutId.getValue() || ''

  if (!quoteId) {
    store.dispatch(setBasicAddressInfo(basicAddressInfo as CustomerAddress))
  }
}

const logout = () => {
  storage.clear()
  store.dispatch(setGlobalIsB3User(false))
}

const initDefaultShipping = async (data: CheckoutStoreSelector, service: CheckoutService) => {

  const { global: { checkoutShippingIsComplete } } = store.getState()

  if (checkoutShippingIsComplete) return

  const consignments = data.getConsignments() ?? []
  const cart = data.getCart()
  const getBillingAddress = data.getBillingAddress()

  const { company } = store.getState()
  const quoteId = storage.quoteCheckoutId.getValue() || ''

  if (!checkoutShippingIsComplete) {
    const {
      defaultShippingAddress,
      defaultBillingAddress,
      addresses: b2bAddresses,
      basicAddressInfo,
    } = company

    let defaultShipping; let defaultBilling: any

    if (quoteId) {
      defaultShipping = basicAddressInfo
      defaultBilling = basicAddressInfo

      const shippingQuoteAddressId = storage.shippingQuoteAddressId.getValue() || ''
      const billingQuoteAddressId = storage.billingQuoteAddressId.getValue() || ''

      if (shippingQuoteAddressId) {
       defaultShipping = b2bAddresses.find((item: Partial<AddressInfo>) => item.addressId && +item.addressId === +shippingQuoteAddressId)
       storage.selectShoppingAddressId.setValue(shippingQuoteAddressId)
      }

      if (billingQuoteAddressId) {
        defaultBilling = b2bAddresses.find((item: Partial<AddressInfo>) => item.addressId && +item.addressId === +billingQuoteAddressId)
        storage.selectBillAddressId.setValue(billingQuoteAddressId)
      }
    } else {
      defaultShipping = defaultShippingAddress.address1 ? defaultShippingAddress : basicAddressInfo
      defaultBilling = defaultBillingAddress.address1 ? defaultBillingAddress : basicAddressInfo
      if (defaultShipping?.addressId) storage.selectShoppingAddressId.setValue(defaultShipping.addressId)
      if (defaultBilling?.addressId) storage.selectBillAddressId.setValue(defaultBilling.addressId)
    }

    // const promises: Array<Promise<CheckoutSelectors>> = []
    const digitalItems = cart?.lineItems.digitalItems ?? []

    if (!consignments.length) {
      const physicalItems = cart?.lineItems.physicalItems ?? []

      const defaultConsignment = physicalItems ? [{
        lineItems: physicalItems.map(({ id: itemId, quantity }) => ({
          itemId,
          quantity,
        })),
        shippingAddress: addresses.convertAddress(defaultShipping)
      }] : []

      if (physicalItems.length) {
        await service.createConsignments(defaultConsignment as ConsignmentsRequestBody)
      }

    } else if (!digitalItems?.length) {
      await service.updateShippingAddress(addresses.convertAddress(defaultShipping))
    }

    // !quoteId && promises.push(service.updateBillingAddress(address.convertAddress(defaultBilling)))
    if (JSON.stringify(getBillingAddress) === '{}') {
      const checkoutId = (window as any)?.checkoutConfig.checkoutId || ''
      const createBillAddress: any = () => axios({
        method: 'post',
        url: `/api/storefront/checkouts/${checkoutId}/billing-address`,
        data: {...addresses.convertAddress(defaultBilling)}
      })

      await createBillAddress()
    } else if (defaultBilling?.firstName || !getBillingAddress?.id) {
      await service.updateBillingAddress(addresses.convertAddress(defaultBilling))
    }

    setAddressExtrasFiles(true, defaultShipping, true)
    defaultBilling && setAddressExtrasFiles(true, defaultBilling, false)
  }
}

// checkout config
export const initStoreCheckoutSettings = async () => {

  try {
    const { global: { isB3User } } = store.getState()

    let checkoutSettings: any = {}

    if (isB3User) {
      const storeCheckoutSettings = await getStoreCheckoutSettings()

      const {
        checkoutShippingAllowNewAddress,
        checkoutShippingAllowSaveAddress,
        checkoutShippingAllowSameAddress,
        checkoutBillingAllowNewAddress,
        checkoutBillingAllowSaveAddress,
      } = storeCheckoutSettings

      checkoutSettings = {
        'shipping.address.save': +checkoutShippingAllowSaveAddress.value === 1,
        'shipping.address.add': +checkoutShippingAllowNewAddress.value === 1,
        'shipping.singleAddress.allowAsBilling': +checkoutShippingAllowSameAddress.value === 1,
        'billing.address.add': +checkoutBillingAllowNewAddress.value === 1,
        'billing.address.save': +checkoutBillingAllowSaveAddress.value === 1,
        ...storeCheckoutSettings,
      }

    } else {
      checkoutSettings = {
        'shipping.address.save': permissions['address.save'],
        'billing.address.save': permissions['address.save'],
        ...permissions,
      }
    }

    store.dispatch(setCheckoutSettings(checkoutSettings))
  } catch (err) {
    console.log('InitStoreCheckoutSettingsError', err)
  }
}

const isShippingComplete = (steps: any, shippingOption: any) => {
  // isInvoice:  if invoice enter
  const isInvoice = steps.some((step: any) =>( step.type === "shipping" && !step.isRequired))
  // Judge when shipping will be completed

  if (!isInvoice && shippingOption?.id) {
    store.dispatch(setCheckoutShippingIsComplete(true))
  } else {
    store.dispatch(setCheckoutShippingIsComplete(false))
  }
}

export default {
  init,
  logout,
  determineOrderType,
  isShippingComplete,
  handleClosingAgency,
  salesRepVerify,
}
